import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity, Globe, BarChart3, HeadphonesIcon,
  ChevronRight, RefreshCw, Palette, X, Settings, Info,
  Upload, Eye, Calendar, MessageSquareHeart, Reply
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrgFeedSettings, useUpdateOrgFeedSettings } from "@/hooks/useOrgFeedSettings";
import { OrgAccessSettingsCard } from "@/components/corporate/organizations/access/OrgAccessSettingsCard";
import { EntraSyncStatusCard } from "@/components/corporate/organizations/EntraSyncStatusCard";
import { useOrgEntraSyncStatus } from "@/hooks/useOrgEntraSyncStatus";
import {
  useOrgReplyDisplayName,
  useUpdateOrgReplyDisplayName,
  REPLY_DISPLAY_NAME_MAX_LENGTH,
} from "@/hooks/useOrgReplyDisplayName";

interface SettingsAndFeaturesTabProps {
  /** Organization GUID — feed settings are scoped per organization. */
  organizationId: string;
}

interface Feature {
  id: string;
  name: string;
  icon: React.ReactNode;
  enabled: boolean;
  description: string;
}

interface BrandingSettings {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  buttonPrimaryColor: string;
  buttonSecondaryColor: string;
  fontFamily: string;
  logo: string | null;
}

interface MarketplaceSettings {
  visibilityMode: "all" | "custom";
  assignedTenants: string[];
  effectiveDate: string;
}

interface LocalizationSettings {
  defaultLanguage: "en" | "ar";
  dateTimeFormat: "12h" | "24h";
  currency: string;
  numberFormat: string;
}

export default function SettingsAndFeaturesTab({ organizationId }: SettingsAndFeaturesTabProps) {
  const [hasChanges, setHasChanges] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Social Feed master switch — real, per-organization, persisted to the CMS API.
  // This is intentionally wired live (independent of the mock "Save All" footer below).
  const {
    data: feedSettings,
    isLoading: isFeedSettingsLoading,
  } = useOrgFeedSettings(organizationId);
  const updateFeedSettings = useUpdateOrgFeedSettings(organizationId);

  const handleSocialFeedToggle = (enabled: boolean) => {
    updateFeedSettings.mutate({
      masterFeedEnabled: enabled,
      feedEnabled: feedSettings?.feedEnabled ?? null,
      allowEmployeePosts: feedSettings?.allowEmployeePosts ?? null,
      allowComments: feedSettings?.allowComments ?? null,
      allowReactions: feedSettings?.allowReactions ?? null,
      allowImagePosts: feedSettings?.allowImagePosts ?? null,
      allowVideoPosts: feedSettings?.allowVideoPosts ?? null,
    });
  };

  // Microsoft Entra sync status — `isEnabled` is the source of truth for "signs in with Entra"
  // (locks the allowed-domain editor up front) and drives the read-only Microsoft sync card.
  const {
    data: entraSyncStatus,
    isLoading: isEntraSyncLoading,
    isError: isEntraSyncError,
  } = useOrgEntraSyncStatus(organizationId);
  // Until the status resolves the org is neither "Entra" nor "not Entra" — keep domains locked.
  const entraCheck = isEntraSyncError ? "failed" : isEntraSyncLoading ? "loading" : undefined;

  // Reply display name — real, per-organization, persisted to the CMS API.
  // The internal team manages any org's value; blank clears to the registered name.
  const {
    data: replyName,
    isLoading: isReplyNameLoading,
  } = useOrgReplyDisplayName(organizationId);
  const updateReplyName = useUpdateOrgReplyDisplayName(organizationId);

  const [replyNameInput, setReplyNameInput] = useState("");
  // Sync the local editor with the server value once it loads / changes.
  useEffect(() => {
    setReplyNameInput(replyName?.replyDisplayName ?? "");
  }, [replyName?.replyDisplayName]);

  const trimmedReplyName = replyNameInput.trim();
  const replyNameTooLong =
    trimmedReplyName.length > REPLY_DISPLAY_NAME_MAX_LENGTH;
  const replyNameDirty =
    trimmedReplyName !== (replyName?.replyDisplayName ?? "");

  const handleSaveReplyName = () => {
    if (replyNameTooLong) return;
    updateReplyName.mutate({ replyDisplayName: replyNameInput });
  };


  // Features state
  const [features, setFeatures] = useState<Feature[]>([
    {
      id: "bookings",
      name: "Bookings",
      icon: <Activity className="h-5 w-5" />,
      enabled: true,
      description: "Allow users to submit and track bookings"
    },
    {
      id: "localization",
      name: "Localization (Enable Arabic RTL)",
      icon: <Globe className="h-5 w-5" />,
      enabled: true,
      description: "Multi-language support with RTL capabilities"
    },
    {
      id: "analytics",
      name: "Analytics Dashboard",
      icon: <BarChart3 className="h-5 w-5" />,
      enabled: false,
      description: "Advanced analytics and reporting features"
    },
    {
      id: "support",
      name: "Support Ticketing",
      icon: <HeadphonesIcon className="h-5 w-5" />,
      enabled: true,
      description: "Integrated support ticket system"
    }
  ]);

  // Branding state
  const [branding, setBranding] = useState<BrandingSettings>({
    primaryColor: "#0EA5E9",
    secondaryColor: "#0369A1",
    accentColor: "#F59E0B",
    backgroundColor: "#FFFFFF",
    buttonPrimaryColor: "#0EA5E9",
    buttonSecondaryColor: "#E5E7EB",
    fontFamily: "Inter",
    logo: null
  });

  // Marketplace visibility state
  const [marketplace, setMarketplace] = useState<MarketplaceSettings>({
    visibilityMode: "all",
    assignedTenants: [],
    effectiveDate: new Date().toISOString().split('T')[0]
  });

  // Localization state
  const [localization, setLocalization] = useState<LocalizationSettings>({
    defaultLanguage: "en",
    dateTimeFormat: "12h",
    currency: "AED",
    numberFormat: "1,234.56"
  });

  // Feature configurations (simplified)
  const [activityConfig, setActivityConfig] = useState({
    approvalWorkflow: "manager",
    autoApprove: false,
    notifyEmail: true,
    notifySMS: false,
    categories: ["Booking", "Inquiry", "Complaint", "Suggestion"]
  });

  const handleFeatureToggle = (featureId: string) => {
    setFeatures(prev => prev.map(feature => 
      feature.id === featureId 
        ? { ...feature, enabled: !feature.enabled }
        : feature
    ));
    setHasChanges(true);
  };

  const handleBrandingChange = (field: keyof BrandingSettings, value: string) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding(prev => ({
          ...prev,
          logo: reader.result as string
        }));
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding(prev => ({
          ...prev,
          logo: reader.result as string
        }));
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveAll = () => {
    console.log('Saving all settings...', { features, branding, marketplace, localization });
    setHasChanges(false);
  };

  const handleCancel = () => {
    setHasChanges(false);
  };

  // Feature configuration drawers (simplified version)
  const renderFeatureDrawer = () => {
    if (!activeDrawer) return null;

    const drawerContent = {
      "bookings": (
        <div className="space-y-6">
          <div className="space-y-3">
            <Label>Approval Workflow</Label>
            <RadioGroup 
              value={activityConfig.approvalWorkflow}
              onValueChange={(value) => {
                setActivityConfig(prev => ({ ...prev, approvalWorkflow: value }));
                setHasChanges(true);
              }}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="auto" id="auto" />
                <Label htmlFor="auto" className="font-normal">Auto-approve all bookings</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="manager" id="manager" />
                <Label htmlFor="manager" className="font-normal">Manager approval required</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="admin" id="admin" />
                <Label htmlFor="admin" className="font-normal">Admin approval required</Label>
              </div>
            </RadioGroup>
          </div>
          <Separator />
          <div className="space-y-3">
            <Label>Notification Settings</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="notify-email"
                  checked={activityConfig.notifyEmail}
                  onCheckedChange={(checked) => {
                    setActivityConfig(prev => ({ ...prev, notifyEmail: checked as boolean }));
                    setHasChanges(true);
                  }}
                />
                <Label htmlFor="notify-email" className="font-normal">Email notifications</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="notify-sms"
                  checked={activityConfig.notifySMS}
                  onCheckedChange={(checked) => {
                    setActivityConfig(prev => ({ ...prev, notifySMS: checked as boolean }));
                    setHasChanges(true);
                  }}
                />
                <Label htmlFor="notify-sms" className="font-normal">SMS notifications</Label>
              </div>
            </div>
          </div>
        </div>
      ),
      // Add other drawer configurations as needed
    };

    const titles = {
      "bookings": "Bookings Configuration",
      "localization": "Localization Settings",
      "analytics": "Analytics Dashboard Configuration",
      "support": "Support Ticketing Settings"
    };

    return (
      <Sheet open={!!activeDrawer} onOpenChange={(open) => !open && setActiveDrawer(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{titles[activeDrawer as keyof typeof titles]}</SheetTitle>
            <SheetDescription>
              Configure feature-specific settings and preferences
            </SheetDescription>
          </SheetHeader>
          
          <div className="py-6">
            {drawerContent[activeDrawer as keyof typeof drawerContent] || (
              <p className="text-muted-foreground">Configuration options coming soon...</p>
            )}
          </div>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setActiveDrawer(null)}>
              Cancel
            </Button>
            <Button onClick={() => {
              console.log(`Saving ${activeDrawer} configuration...`);
              setActiveDrawer(null);
            }}>
              Save Changes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  };

  return (
    <div className="relative pb-20">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Settings & Features</h2>
        <p className="text-sm text-muted-foreground">
          Manage features, branding, and client-specific settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Features & Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Features Panel */}
          <Card>
            <CardHeader>
              <CardTitle>Features</CardTitle>
              <CardDescription>
                Enable or disable features and configure their settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {features.map((feature) => (
                <div key={feature.id} className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg text-primary">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-medium">{feature.name}</h4>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={feature.enabled}
                      onCheckedChange={() => handleFeatureToggle(feature.id)}
                      aria-label={`Toggle ${feature.name}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveDrawer(feature.id)}
                      disabled={!feature.enabled}
                    >
                      Configure
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Social Feed master switch — real, persisted per-organization */}
              <div
                className="flex items-center justify-between p-4 rounded-lg border"
                data-testid="social-feed-feature-row"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <MessageSquareHeart className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium">Social Feed</h4>
                    <p className="text-sm text-muted-foreground">
                      Master switch for the employee social feed, stickers and kudos.
                      When off, the feed is hidden for all employees in this organization.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isFeedSettingsLoading ? (
                    <Skeleton className="h-6 w-11 rounded-full" />
                  ) : (
                    <Switch
                      checked={!!feedSettings?.masterFeedEnabled}
                      onCheckedChange={handleSocialFeedToggle}
                      disabled={updateFeedSettings.isPending}
                      aria-label="Toggle Social Feed"
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sign-in & access — real, persisted per-organization. Its "Perks enabled" switch
              replaces the former mock "Global Marketplace Access" row (one perks toggle only). */}
          <OrgAccessSettingsCard
            organizationId={organizationId}
            hasEntraIntegration={!!entraSyncStatus?.isEnabled}
            entraCheck={entraCheck}
          />

          {/* Microsoft sync — read-only, shown only when Entra sync is enabled */}
          <EntraSyncStatusCard status={entraSyncStatus} />

          {/* Reply display name — real, persisted per-organization */}
          <Card data-testid="reply-display-name-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Reply className="h-4 w-4" />
                Reply display name
              </CardTitle>
              <CardDescription>
                The name this organization&apos;s replies to employee feedback
                are signed with.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {isReplyNameLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="reply-display-name">Display name</Label>
                    <Input
                      id="reply-display-name"
                      value={replyNameInput}
                      placeholder="e.g. People Team"
                      onChange={(e) => setReplyNameInput(e.target.value)}
                      aria-label="Reply display name"
                      aria-invalid={replyNameTooLong}
                      disabled={updateReplyName.isPending}
                    />
                    <p
                      className={cn(
                        "text-sm",
                        replyNameTooLong
                          ? "text-destructive"
                          : "text-muted-foreground",
                      )}
                    >
                      {replyNameTooLong
                        ? `Keep it to ${REPLY_DISPLAY_NAME_MAX_LENGTH} characters or fewer.`
                        : "Shown on this organization's replies to employee feedback — leave empty to use its registered company name."}
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveReplyName}
                      disabled={
                        updateReplyName.isPending ||
                        replyNameTooLong ||
                        !replyNameDirty
                      }
                    >
                      {updateReplyName.isPending ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Marketplace Visibility Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Marketplace Visibility
              </CardTitle>
              <CardDescription>
                Control marketplace item visibility
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>Visibility Mode</Label>
                <Select
                  value={marketplace.visibilityMode}
                  onValueChange={(value: "all" | "custom") => {
                    setMarketplace(prev => ({ ...prev, visibilityMode: value }));
                    setHasChanges(true);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Visible to All Tenants</SelectItem>
                    <SelectItem value="custom">Custom Tenant Assignment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {marketplace.visibilityMode === "custom" && (
                <>
                  <div className="space-y-3">
                    <Label>Assigned Tenants</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tenants..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tenant1">Tenant 1</SelectItem>
                        <SelectItem value="tenant2">Tenant 2</SelectItem>
                        <SelectItem value="tenant3">Tenant 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="effective-date">Effective Date</Label>
                    <div className="relative">
                      <Input
                        id="effective-date"
                        type="date"
                        value={marketplace.effectiveDate}
                        onChange={(e) => {
                          setMarketplace(prev => ({ ...prev, effectiveDate: e.target.value }));
                          setHasChanges(true);
                        }}
                        className="pr-10"
                      />
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Localization Defaults Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Localization Defaults
              </CardTitle>
              <CardDescription>
                Set default language and format preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Label>Default Language</Label>
                  <Select
                    value={localization.defaultLanguage}
                    onValueChange={(value: "en" | "ar") => {
                      setLocalization(prev => ({ ...prev, defaultLanguage: value }));
                      setHasChanges(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="ar">Arabic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Date/Time Format</Label>
                  <Select
                    value={localization.dateTimeFormat}
                    onValueChange={(value: "12h" | "24h") => {
                      setLocalization(prev => ({ ...prev, dateTimeFormat: value }));
                      setHasChanges(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12h">12-hour (AM/PM)</SelectItem>
                      <SelectItem value="24h">24-hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Currency</Label>
                  <Select
                    value={localization.currency}
                    onValueChange={(value) => {
                      setLocalization(prev => ({ ...prev, currency: value }));
                      setHasChanges(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Number Format</Label>
                  <Select
                    value={localization.numberFormat}
                    onValueChange={(value) => {
                      setLocalization(prev => ({ ...prev, numberFormat: value }));
                      setHasChanges(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1,234.56">1,234.56</SelectItem>
                      <SelectItem value="1.234,56">1.234,56</SelectItem>
                      <SelectItem value="1 234.56">1 234.56</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Branding & Preview */}
        <div className="space-y-6">
          {/* Branding Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Branding
              </CardTitle>
              <CardDescription>
                Customize the visual appearance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Upload */}
              <div>
                <Label>Logo</Label>
                <div 
                  className={cn(
                    "mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                    isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"
                  )}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('logo-upload')?.click()}
                >
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  {branding.logo ? (
                    <div className="relative">
                      <img 
                        src={branding.logo} 
                        alt="Logo preview" 
                        className="h-20 mx-auto object-contain"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-0 right-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBranding(prev => ({ ...prev, logo: null }));
                          setHasChanges(true);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="py-4">
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Drag and drop or click to upload
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Color Pickers */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="primary-color">Primary Color</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <Input
                      id="primary-color"
                      type="color"
                      value={branding.primaryColor}
                      onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
                      className="w-16 h-9 p-1"
                    />
                    <Input
                      value={branding.primaryColor}
                      onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
                      placeholder="#0EA5E9"
                      className="flex-1 h-9"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="secondary-color">Secondary Color</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <Input
                      id="secondary-color"
                      type="color"
                      value={branding.secondaryColor}
                      onChange={(e) => handleBrandingChange('secondaryColor', e.target.value)}
                      className="w-16 h-9 p-1"
                    />
                    <Input
                      value={branding.secondaryColor}
                      onChange={(e) => handleBrandingChange('secondaryColor', e.target.value)}
                      placeholder="#0369A1"
                      className="flex-1 h-9"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="accent-color">Accent Color</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <Input
                      id="accent-color"
                      type="color"
                      value={branding.accentColor}
                      onChange={(e) => handleBrandingChange('accentColor', e.target.value)}
                      className="w-16 h-9 p-1"
                    />
                    <Input
                      value={branding.accentColor}
                      onChange={(e) => handleBrandingChange('accentColor', e.target.value)}
                      placeholder="#F59E0B"
                      className="flex-1 h-9"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="button-color">Button Color</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <Input
                      id="button-color"
                      type="color"
                      value={branding.buttonPrimaryColor}
                      onChange={(e) => handleBrandingChange('buttonPrimaryColor', e.target.value)}
                      className="w-16 h-9 p-1"
                    />
                    <Input
                      value={branding.buttonPrimaryColor}
                      onChange={(e) => handleBrandingChange('buttonPrimaryColor', e.target.value)}
                      placeholder="#0EA5E9"
                      className="flex-1 h-9"
                    />
                  </div>
                </div>
              </div>

              {/* Font Family */}
              <div>
                <Label>Font Family</Label>
                <Select
                  value={branding.fontFamily}
                  onValueChange={(value) => handleBrandingChange('fontFamily', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inter">Inter</SelectItem>
                    <SelectItem value="Roboto">Roboto</SelectItem>
                    <SelectItem value="Open Sans">Open Sans</SelectItem>
                    <SelectItem value="Lato">Lato</SelectItem>
                    <SelectItem value="Montserrat">Montserrat</SelectItem>
                    <SelectItem value="Poppins">Poppins</SelectItem>
                    <SelectItem value="Raleway">Raleway</SelectItem>
                    <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                    <SelectItem value="Source Sans Pro">Source Sans Pro</SelectItem>
                    <SelectItem value="Work Sans">Work Sans</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Branding Preview */}
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Preview</span>
                <Button size="icon" variant="ghost" onClick={() => console.log('Refresh preview...')}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Preview */}
              <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                {branding.logo ? (
                  <img 
                    src={branding.logo} 
                    alt="Client Logo" 
                    className="h-16 object-contain"
                  />
                ) : (
                  <img 
                    src="/api/placeholder/120/60" 
                    alt="Client Logo" 
                    className="h-16 object-contain"
                  />
                )}
              </div>

              {/* Sample Header */}
              <div className="space-y-2">
                <Label>Header Preview</Label>
                <div className="border rounded-lg overflow-hidden">
                  <div 
                    className="text-white p-3 flex items-center justify-between"
                    style={{ backgroundColor: branding.primaryColor }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-white/20 rounded" />
                      <span className="font-medium" style={{ fontFamily: branding.fontFamily }}>
                        Client Portal
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-20 h-6 bg-white/20 rounded" />
                      <div className="w-20 h-6 bg-white/20 rounded" />
                    </div>
                  </div>
                  <div className="bg-gray-100 p-4">
                    <Button 
                      className="mr-2"
                      style={{ backgroundColor: branding.buttonPrimaryColor }}
                    >
                      Primary Button
                    </Button>
                    <Button 
                      variant="outline"
                      style={{ borderColor: branding.secondaryColor, color: branding.secondaryColor }}
                    >
                      Secondary Button
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Feature Configuration Drawer */}
      {renderFeatureDrawer()}

      {/* Sticky Footer */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 flex items-center justify-end gap-2 shadow-lg">
          <div className="container max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              You have unsaved changes
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={handleCancel}>
                Cancel Changes
              </Button>
              <Button onClick={handleSaveAll}>
                Save All
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}